import { createContext, useContext, useState, ReactNode } from 'react';

type Language = 'en' | 'sl';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations = {
  en: {
    nav_home: 'Home',
    nav_about: 'About',
    nav_research: 'Research',
    nav_news: 'News',
    nav_contact: 'Contact',
    hero_headline: 'Empowering Innovation\nthrough AI Education',
    hero_tagline: 'Accelerating breakthroughs in artificial intelligence and digital literacy.',
    hero_cta: 'Learn More',
    about_title: 'Our Mission',
    about_text: 'Our research focuses on developing innovative solutions that drive progress in educational technology trough academia.',
    research_title: 'Our Research Areas',
    research_intro: 'We are actively exploring the following research areas to push the boundaries of AI and digital literacy:',
    research_nlp: 'How To Communicate With AI',
    research_nlp_desc: 'Advancing our understanding of how AI works and how to most effectively manage AI tools.',
    research_cv: 'Education',
    research_cv_desc: 'Actively teaching students and teachers at academic institutions',
    research_rl: 'Innovation',
    research_rl_desc: 'Pushing boundaries in AI research and development of AI tools.',
    research_xai: 'Path to Utopia',
    research_xai_desc: 'Building partnerships across academia, industry and individually.',
    news_title: 'Activities',
    news_intro: 'Stay informed about the latest developments in AI research and our contributions to the field.',
    cta_title: 'Get Involved',
    cta_text: 'Join our community of researchers, students, and industry professionals to collaborate on cutting-edge projects and stay ahead of the curve in AI and digital literacy.',
    cta_button: 'Join Our Community',
    partners_title: 'Our Partners',
    footer_copyright: '2025 Institute AI Runner for Digital Literacy and Artificial Intelligence. All rights reserved.',
    footer_address: 'Ptuj, Slovenia',
    footer_email: 'info@zavoddpui.com',
  },
  sl: {
    nav_home: 'Domov',
    nav_about: 'O nas',
    nav_research: 'Raziskave',
    nav_news: 'Novice',
    nav_contact: 'Kontakt',
    partners_title: 'Naši Partnerji',
    hero_headline: `Spodbujanje Inovativnosti z
Izobraževanjem o UI`,
    hero_tagline: `Pospešujemo preboje na področju umetne inteligence in digitalne pismenosti
z inovativnimi AI delavnicami in ustvarjanjem AI orodij.`,
    hero_cta: 'Več o tem',
    about_title: 'Naše poslanstvo',
    about_text: 'Naše raziskave se osredotočajo na razvoj inovativnih rešitev, ki spodbujajo napredek v izobraževalni tehnologiji.',
    research_title: 'Naša raziskovalna področja',
    research_intro: 'Aktivno se posvečamo raziskavam za širitev meja UI in digitalne pismenosti:',
    research_nlp: 'Kako komunicirati z AI',
    research_nlp_desc: 'Napredovanje v razumevanju kako UI deluje in kako najučinkoviteje upravljati z UI orodji.',
    research_cv: 'Izobraževanje',
    research_cv_desc: 'Aktivno poučevanje študentov in učiteljev na akademskih ustanovah',
    research_rl: 'Inovacija',
    research_rl_desc: 'Potekanje meja v raziskavah UI in razvoju UI orodij.',
    research_xai: 'Pot v utopio',
    research_xai_desc: 'Ustvarjanje partnerstev med akademskimi ustanovami, industrijo in posamezniki.',
    news_title: 'Ostanite obveščeni',
    news_intro: 'Bodite obveščeni o najnovejših dogajanjih na področju raziskav UI in naših prispevkih.',
    cta_title: 'Pridružite se nam',
    cta_text: 'Pridružite se naši skupnosti raziskovalcev, študentov in strokovnjakov iz industrije za sodelovanje pri najsodobnejših projektih in ostanite v koraku s področjem UI in digitalne pismenosti.',
    cta_button: 'Pridruži se skupnosti',
    footer_copyright: '2025 Zavod AI Runner za Digitalno Pismenost in Umetno Inteligenco. Vse pravice pridržane.',
    footer_address: 'Ptuj, Slovenija',
    footer_email: 'info@zavoddpui.com',
  },
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>('en');

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations.en] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
